/**
 * DeckImportPreviewTable - Table showing cards to be imported with inventory status
 *
 * Displays columns for exact/any version ownership and pagination support.
 */
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';

import clsx from 'clsx';
import { IoCheckmark } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import TablePagination from '@/components/TablePagination/TablePagination';
import { DECK_ZONE_LABELS } from '@/lib/deckUtils';
import type { MinimalCard } from '@/services/cardDatabase';
import type { DeckZone } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export interface DeckImportPreviewCard {
  name: string;
  setCode: string | null;
  collectorNumber: string | null;
  quantity: number;
  zone: DeckZone;
  scryfallId?: string;
  matched: MinimalCard | null;
  matchType: 'exact' | 'name' | 'fuzzy' | 'none';
  hasExactVersion: boolean;
  hasAnyVersion: boolean;
  ownedExactQty: number;
  ownedTotalQty: number;
  /** True if a different version was substituted for the requested one */
  versionSubstituted?: boolean;
  /** Original requested set code (if a specific version was requested) */
  requestedSetCode?: string | null;
  /** Original requested collector number (if a specific version was requested) */
  requestedCollectorNumber?: string | null;
}

export interface DeckImportPreviewTableProps {
  cards: DeckImportPreviewCard[];
}

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 50;

// ============================================================================
// Component
// ============================================================================

export default function DeckImportPreviewTable({ cards }: DeckImportPreviewTableProps): ReactElement {
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination
  const totalPages = Math.ceil(cards.length / PAGE_SIZE);
  const paginatedCards = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return cards.slice(start, start + PAGE_SIZE);
  }, [cards, currentPage]);

  return (
    <div className="border-storm overflow-hidden rounded-xl border">
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate sticky top-0 z-10">
            <tr className="text-ash text-left text-xs tracking-wider uppercase">
              <th className="w-10 px-2 py-3 text-center" title="Exact version in collection">
                E
              </th>
              <th className="w-10 px-2 py-3 text-center" title="Any version in collection">
                A
              </th>
              <th className="px-4 py-3">Card Name</th>
              <th className="w-16 px-4 py-3">Set</th>
              <th className="w-12 px-4 py-3">#</th>
              <th className="w-10 px-4 py-3 text-center">Qty</th>
              <th className="w-24 px-4 py-3">Zone</th>
            </tr>
          </thead>
          <tbody className="divide-storm divide-y">
            {paginatedCards.map((card, index) => (
              <DeckImportPreviewRow key={`${card.name}-${card.setCode}-${currentPage}-${index}`} card={card} />
            ))}
          </tbody>
        </table>
      </div>

      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={cards.length}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}

// ============================================================================
// Row Component
// ============================================================================

interface DeckImportPreviewRowProps {
  card: DeckImportPreviewCard;
}

function DeckImportPreviewRow({ card }: DeckImportPreviewRowProps): ReactElement {
  const rowClass = clsx(
    'group transition-colors',
    card.hasExactVersion && 'bg-success/5 hover:bg-success/10',
    !card.hasExactVersion && card.hasAnyVersion && 'bg-aether/5 hover:bg-aether/10',
    !card.hasExactVersion && !card.hasAnyVersion && card.matched && 'bg-warning/5 hover:bg-warning/10',
    !card.matched && 'bg-danger/5 hover:bg-danger/10',
  );

  const displayName = card.name || card.matched?.name || '';
  const cellContent = <span className="text-pearl font-medium">{displayName}</span>;

  return (
    <tr className={rowClass}>
      {/* Exact version column */}
      <td className="px-2 py-3 text-center">{card.hasExactVersion && <IoCheckmark className="text-success mx-auto h-4 w-4" />}</td>

      {/* Any version column */}
      <td className="px-2 py-3 text-center">{card.hasAnyVersion && <IoCheckmark className="text-aether mx-auto h-4 w-4" />}</td>

      {/* Card name with hover preview */}
      <td className="px-4 py-3">
        {card.matched ? (
          <CardHoverPreview imageUris={card.matched.image_uris} name={card.matched.name}>
            {cellContent}
          </CardHoverPreview>
        ) : (
          <div className="flex items-center gap-2">
            {cellContent}
            <span className="text-danger text-xs">(not found)</span>
          </div>
        )}
        {card.matched && card.matchType === 'fuzzy' && <span className="text-ash ml-2 text-xs">→ {card.matched.name}</span>}
      </td>

      {/* Set code - show "FROM → TO" if version was substituted */}
      <td className="text-silver px-4 py-3 text-xs uppercase">
        {card.versionSubstituted && card.requestedSetCode ? (
          <span>
            <span className="text-ash line-through">{card.requestedSetCode}</span>
            <span className="text-aether"> → {card.matched?.set}</span>
          </span>
        ) : (
          (card.matched?.set ?? card.setCode ?? '—')
        )}
      </td>

      {/* Collector number - show "FROM → TO" if version was substituted */}
      <td className="text-ash px-4 py-3 font-mono text-xs">
        {card.versionSubstituted && card.requestedCollectorNumber ? (
          <span>
            <span className="line-through">{card.requestedCollectorNumber}</span>
            <span className="text-aether"> → {card.matched?.collector_number}</span>
          </span>
        ) : (
          (card.matched?.collector_number ?? card.collectorNumber ?? '—')
        )}
      </td>

      {/* Quantity */}
      <td className="text-pearl px-4 py-3 text-center font-medium">{card.quantity}</td>

      {/* Zone */}
      <td className="text-silver px-4 py-3">
        <span className="text-xs">{DECK_ZONE_LABELS[card.zone]}</span>
      </td>
    </tr>
  );
}
